import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  DefaultApi,
  IdObject,
  User,
  UserBody,
} from "@vviinn/user-managment-client-ts";
import { FetchableState } from "..";
import { ApiRequest, serializeModel } from "../../../api";

type UsersState = FetchableState & {
  users: User[];
};

const initialState: UsersState = {
  users: [],
  loading: false,
  error: false,
  status: "idle",
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    addUserCompanyRole(state, action) {
      const target = state.users.find((u) => u.uid === action.payload.userId);
      if (!target) return;
      const { id, role } = action.payload;
      // A user holds one role per company — assigning a new one replaces it.
      target.assignedCompanies = [
        ...target.assignedCompanies.filter((c) => c.id !== id),
        { id, role },
      ];
    },
    deleteUserCompanyRole(state, action) {
      const target = state.users.find((u) => u.uid === action.payload.userId);
      if (!target) return;
      target.assignedCompanies = target.assignedCompanies.filter(
        (c) => c.id !== action.payload.id,
      );
    },
    addUserGlobalRole(state, action) {
      const target = state.users.find((u) => u.uid === action.payload.userId);
      if (!target) return;
      target.roles.push(action.payload.role);
    },
    deleteUserGlobalRole(state, action) {
      const target = state.users.find((u) => u.uid === action.payload.userId);
      if (!target) return;
      target.roles = target.roles.filter(
        (role) => role !== action.payload.role,
      );
    },
    changeUserPosition(state, action) {
      const target = state.users.find((u) => u.uid === action.payload.userId);
      if (!target) return;
      target.position = action.payload.position;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchUsers.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.status = "loading";
    });
    builder.addCase(fetchUsers.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.status = "error";
      state.users = initialState.users;
    });
    builder.addCase(fetchUsers.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.error = false;
      state.users = payload;
      state.status = "fulfilled";
    });

    builder.addCase(addUser.fulfilled, (state, { payload }) => {
      state.users.push(payload);
    });

    builder.addCase(deleteUser.fulfilled, (state, { payload }) => {
      const userId = payload.id;
      state.users = state.users.filter((u) => u.uid !== userId);
    });
  },
});

export default usersSlice.reducer;

export const {
  addUserCompanyRole,
  deleteUserCompanyRole,
  addUserGlobalRole,
  deleteUserGlobalRole,
  changeUserPosition,
} = usersSlice.actions;

export const fetchUsers = createAsyncThunk(
  "users/fetch",
  async (api: DefaultApi) => {
    const response = await api.getUsers();
    return response.value.map(serializeModel);
  },
);

export const addUser = createAsyncThunk(
  "users/add",
  async ({ api, value }: ApiRequest<UserBody>) => {
    const response = await api.createUser(value);
    return serializeModel(response.value);
  },
);

export const deleteUser = createAsyncThunk(
  "users/delete",
  async ({ api, value }: ApiRequest<string>): Promise<IdObject> => {
    const response = await api.deleteUser(value);
    return serializeModel(response.value);
  },
);
